import { effect, Injectable, signal, WritableSignal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AllowedExtensions, IChatAttachment, IChatAttachments, IChatMessage, IChatMessages, IChatTempMessage, IMentionableUsers, INewChatMessage } from '../models/chat';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { SocketGroup, SocketService } from './socket.service';
import { IChat, IChatsResponse } from '../components/all-planogram-chats/chat';
import { Utils } from './utils.service';
import { LinkDtypeElement } from '../components/dtypes/link-dtype/link-dtype.component';
import { IPlanogramLink } from '../../planogram/models/planogramListItem';
import { NOT_CANCELLED_HEADER } from '../interceptors/auth.interceptor';

export const IMAGE_EXTENSIONS: Array<AllowedExtensions> = [
    AllowedExtensions.Jpg,
    AllowedExtensions.Jpeg,
    AllowedExtensions.Png,
    AllowedExtensions.Svg,
    AllowedExtensions.Webp,
    AllowedExtensions.Gif,
    AllowedExtensions.Heic
];

export const DOCUMENT_EXTENSIONS: Array<AllowedExtensions> = [
    AllowedExtensions.Pdf,
    AllowedExtensions.Doc,
    AllowedExtensions.Docx,
    AllowedExtensions.Xls,
    AllowedExtensions.Xlsx,
    AllowedExtensions.Pptx,
    AllowedExtensions.Txt
];

export const VIDEO_EXTENSIONS: Array<AllowedExtensions> = [
    AllowedExtensions.Mp4,
    AllowedExtensions.Mov
];

export const ARCHIVE_EXTENSIONS: Array<AllowedExtensions> = [
    AllowedExtensions.Zip,
    AllowedExtensions.Rar
];

export const FILE_EXTENSIONS_ALLOWED: Array<AllowedExtensions | string> = [
    ...IMAGE_EXTENSIONS,
    ...DOCUMENT_EXTENSIONS,
    ...VIDEO_EXTENSIONS,
    ...ARCHIVE_EXTENSIONS
];


const ROOT_URL: string = '/api/v1/chats/';

@Injectable()
export class ChatService {

    private _id: WritableSignal<number> = signal<number>(null);
    private _tempMessage: WritableSignal<IChatTempMessage> = signal<IChatTempMessage>({ message: '', files: [] });

    public focusMessageId: WritableSignal<number> = signal<number>(null);
    public isLoadingNext: WritableSignal<boolean> = signal<boolean>(false);
    public isLoadingPrevious: WritableSignal<boolean> = signal<boolean>(false);
    public mentionableUsers: WritableSignal<Array<IMentionableUsers>> = signal<Array<IMentionableUsers>>([]);
    public pinnedMessages: WritableSignal<Array<IChatMessage>> = signal<Array<IChatMessage>>(null);
    public pendingRestoreMessageIds: WritableSignal<Set<number>> = signal<Set<number>>(new Set<number>());
    public highlightAnchor: WritableSignal<boolean> = signal<boolean>(false);
    public activeChatTab: WritableSignal<number> = signal<number>(0);
    public mediaSearchFilter: WritableSignal<string> = signal<string>('');
    public documentsSearchFilter: WritableSignal<string> = signal<string>('');
    public searchPanelQuery: WritableSignal<string> = signal<string>('');
    public searchPanelOpen: WritableSignal<boolean> = signal<boolean>(false);
    public searchPanelResultsVisible: WritableSignal<boolean> = signal<boolean>(false);

    public checkScrollBottomPosition$: Subject<void> = new Subject<void>();
    public inputMessage$: Subject<void> = new Subject<void>();
	public newMessage$: Subject<INewChatMessage> = new Subject<INewChatMessage>();
    public loadLastMessages$: Subject<void> = new Subject<void>();
    public loadNextMessages$: Subject<void> = new Subject<void>();
    public loadAroundMessage$: Subject<number> = new Subject<number>();
    public loadPreviousMessages$: Subject<void> = new Subject<void>();
    public replyMessage$: Subject<IChatMessage> = new Subject<IChatMessage>();
    public editMessage$: Subject<IChatMessage> = new Subject<IChatMessage>();
    public scrollToDay$: Subject<string> = new Subject<string>();
    public scrollToBottom$: Subject<void> = new Subject<void>();
    public scrollToMessage$: Subject<number> = new Subject<number>();
    public updateMessageRequest$: Subject<{ message: IChatMessage, payload: Record<string, unknown> }> =
        new Subject<{ message: IChatMessage, payload: Record<string, unknown> }>();
    public deleteAttachment$: Subject<IChatAttachment> = new Subject<IChatAttachment>();
    public reloadAttachments$: Subject<void> = new Subject<void>();
	public replyToMessage: WritableSignal<IChatMessage | null> = signal<IChatMessage | null>(null);

    set id(value: number) {
        if (!Utils.isNullOrUndefined(this.id) && this.id !== value) {
            this.socketService.deleteSubscription(SocketGroup.Chat, { chat_id: this.id });
        }
        this._id.set(value);
    }

    get id(): number {
        return this._id();
    }

    set tempMessage(value: IChatTempMessage) {
        this._tempMessage.set({
            message: value?.message || '',
            files: value?.files || [],
            replyMessage: value?.replyMessage ?? null,
            editingMessage: value?.editingMessage ?? null
        });
    }

    get tempMessage(): IChatTempMessage {
        return this._tempMessage();
    }

    constructor(private http: HttpClient, private socketService: SocketService) {
        effect((): void => {
           if (!this._id()) return;
           this.socketService.addSubscription(SocketGroup.Chat, { chat_id: this.id });
           this.getMentionableUsers(this.id).subscribe((users: Array<IMentionableUsers>): void => {
                this.mentionableUsers.set(users);
           });
        });

        effect((): void => {
            if (!this._id()) return;
            this.getPinnedMessages(this.id).subscribe((messages: Array<IChatMessage>): void => {
                this.pinnedMessages.set(messages);
            });
        });
    }

    getIconForFileExtension(extension: string): string {
        const fileExtensionsIcons: Record<string, string> = {
            'pdf': 'filePDF',
            'doc': 'fileDOC',
            'docx': 'fileDOC',
            'xlsx': 'fileXLS',
            'xls': 'fileXLS',
            'pptx': 'filePPT',
            'txt': 'fileTXT'
        }   
        return fileExtensionsIcons[extension] || 'description';
    }

    public getAttachments(id: number, params: any): Observable<IChatAttachments> {
        const httpParams: HttpParams = new HttpParams({ fromObject: params });
		return this.http.get<IChatAttachments>(`${ROOT_URL}${id}/attachments/`, { params: httpParams });
	}

    public deleteAttachment(id: number, attachmentId: number): Observable<void> {
        return this.http.delete<void>(`${ROOT_URL}${id}/attachments/${attachmentId}/`);
    }

    public getMessages(id: number, params: any): Observable<IChatMessages> {
        const httpParams: HttpParams = new HttpParams({ fromObject: params });
		return this.http.get<IChatMessages>(`${ROOT_URL}${id}/messages/`, { params: httpParams });
	}

    public getMessagesAround(id: number, anchor: 'date' | 'message' | 'unread', params: any = {}): Observable<IChatMessages> {
        const httpParams: HttpParams = new HttpParams({ fromObject: { anchor, ...params } });
		return this.http.get<IChatMessages>(`${ROOT_URL}${id}/messages/around/`, { params: httpParams });
	}

    public getMentionableUsers(id: number): Observable<Array<IMentionableUsers>> {
        return this.http.get<Array<IMentionableUsers>>(`${ROOT_URL}${id}/mentionable-users/`);
    }

    public getPinnedMessages(id: number): Observable<Array<IChatMessage>> {
		return this.http.get<Array<IChatMessage>>(`${ROOT_URL}${id}/messages/pinned/`);
    }

	public sendMessage(id: number, message: INewChatMessage): Observable<IChatMessage> {
        const headers: HttpHeaders = new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true');
		return this.http.post<IChatMessage>(`${ROOT_URL}${id}/messages/`, message, { headers });
	}

    public loadNextMessages(url: string): Observable<IChatMessages> {
        this.isLoadingNext.set(true);
		return this.http.get<IChatMessages>(url);
	}

    public loadPreviousMessages(url: string): Observable<IChatMessages> {
        this.isLoadingPrevious.set(true);
		return this.http.get<IChatMessages>(url);
	}

    public loadMessagesPage(url: string): Observable<IChatMessages> {
        return this.http.get<IChatMessages>(url);
    }

	public markAsRead(id: number, params: any = {}): Observable<void> {
        const httpParams: HttpParams = new HttpParams({ fromObject: params });
		return this.http.post<void>(`${ROOT_URL}${id}/messages/read/`, {}, { params: httpParams });
	}

	public getChats(params: any = {}): Observable<IChatsResponse> {
		const httpParams: HttpParams = new HttpParams({ fromObject: params });
		return this.http.get<IChatsResponse>(ROOT_URL, { params: httpParams });
	}

	public getAvailableChats(params: any = {}): Observable<IChatsResponse> {
		const httpParams: HttpParams = new HttpParams({ fromObject: params });
		return this.http.get<IChatsResponse>(`${ROOT_URL}available/`, { params: httpParams });
	}

	public loadNextChats(url: string): Observable<IChatsResponse> {
		return this.http.get<IChatsResponse>(url);
	}

	public joinChats(params: any = {}): Observable<void> {
		const httpParams: HttpParams = new HttpParams({ fromObject: params });
		return this.http.post<void>(`${ROOT_URL}join/`, {}, { params: httpParams });
	}

    public pinChat(id: number, isPinned: boolean): Observable<void> {
        return this.http.patch<void>(`${ROOT_URL}${id}/`, { is_pinned: isPinned });
    }

    public pinMessage(chatId: number, messageId: number): Observable<void> {
        return this.http.post<void>(`${ROOT_URL}${chatId}/messages/${messageId}/pin/`, {});
    }

    public unpinMessage(chatId: number, messageId: number): Observable<void> {
        return this.http.post<void>(`${ROOT_URL}${chatId}/messages/${messageId}/unpin/`, {});
    }

    public deleteMessage(chatId: number, messageId: number): Observable<void> {
        return this.http.delete<void>(`${ROOT_URL}${chatId}/messages/${messageId}/`);
    }

    public restoreMessage(chatId: number, messageId: number): Observable<void> {
        return this.http.post<void>(`${ROOT_URL}${chatId}/messages/${messageId}/restore/`, {});
    }

    public addPendingRestoreMessage(messageId: number): void {
        this.pendingRestoreMessageIds.update((value: Set<number>): Set<number> => {
            const next: Set<number> = new Set<number>(value);
            next.add(messageId);
            return next;
        });
    }

    public removePendingRestoreMessage(messageId: number): void {
        this.pendingRestoreMessageIds.update((value: Set<number>): Set<number> => {
            const next: Set<number> = new Set<number>(value);
            next.delete(messageId);
            return next;
        });
    }

	public createPlanogramChat(planogramId: number): Observable<{ id: number }> {
		return this.http.post<{ id: number }>(`${ROOT_URL}`, { type: 'PLANOGRAM', planogram: planogramId });
	}

    public deleteFile(key: string): Observable<void> {
        return this.http.post<void>(`${ROOT_URL}delete-uploaded-attachment/`, { key });
    }

    public uploadFile(file: File): Observable<{ key: string, file: string }> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<{ key: string, file: string }>(`${ROOT_URL}upload-attachment/`, formData);
    }

    public getPlanogramLink(id: number): Observable<LinkDtypeElement<IPlanogramLink>> {
        return this.http.get<LinkDtypeElement<IPlanogramLink>>(`${ROOT_URL}${id}/planogram-link/`);
    }
 
	public updateChat(id: number, params: Partial<IChat>): Observable<void> {
        return this.http.patch<void>(`${ROOT_URL}${id}/`, params);
    }

    public updateMessage(chatId: number, messageId: number, params: Partial<IChatMessage>): Observable<IChatMessage> {
        return this.http.patch<IChatMessage>(`${ROOT_URL}${chatId}/messages/${messageId}/`, params);
    }

    public addReaction(chatId: number, messageId: number, emoji: string): Observable<{ id: number; emoji: string }> {
        return this.http.post<{ id: number; emoji: string }>(`${ROOT_URL}${chatId}/messages/${messageId}/reactions/`, { emoji });
    }

    public removeReaction(chatId: number, messageId: number, emoji: string): Observable<void> {
        return this.http.delete<void>(`${ROOT_URL}${chatId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}/`);
    }

    public sendTyping(id: number): Observable<void> {
        return this.http.post<void>(`${ROOT_URL}${id}/typing/`, {});
    }

    ngOnDestroy(): void {
		this.socketService.deleteSubscription(SocketGroup.Chat, { chat_id: this.id });
	}
}